import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import GridLayout from 'react-grid-layout';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import DashboardClock from '../../dashboard/DashboardClock';
import DashboardQuickStats from '../../dashboard/DashboardQuickStats';
import LiveFeed from '../../dashboard/LiveFeed/LiveFeed';
import DashboardAnalyticsWidget from '../../dashboard/DashboardAnalyticsWidget';
import DashboardTasksWidget from '../../dashboard/DashboardTasksWidget';
import DashboardCustomExportWidget from '../../dashboard/DashboardCustomExportWidget';

import OnTraccrEmpty from '../OnTraccrEmpty';
import DashboardShortListWidget from '../../dashboard/DashboardShortlistWidget';
import BoardCardDrawer from '../../boards/BoardCardDrawer';
import useBoardPermissions from '../hooks/useBoardPermissions';
import CustomFieldFormMappingDrawer from '../../forms/CustomFieldFormMappingDrawer/CustomFieldFormMappingDrawer';
import FormResponder from '../../forms/FormResponder';
import { closeFormDrawer, getFormMappings } from '../../boards/state/boards.actions';
import Permissions from '../../auth/Permissions';

const RESIZE_HANDLES = ['s', 'e', 'se'];
const NUM_COLS = 12;
const ITEM_MARGIN = 10;
const MARGINS = [10, 10];

export default function DashboardContainer({
  currentLayout,
  setCurrentLayout,
  savedReports,
  analyticsData,
  selectedDashboardId,
  isEdit,
  projectId,
  cardId,
  analyticsType,
  layoutKey,
  rightMargin = 0,
  ourUsers,
  history,
  onClockIn,
  onClockOut,
  onSwitch,
  onBreakStart,
  onBreakEnd,
  onUserClick,
  onEditWidget,
}) {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const phases = useSelector((state) => state.costcodes.phases);
  const projects = useSelector((state) => state.projects.projects);
  const unions = useSelector((state) => state.unions);
  const approvals = useSelector((state) => state.approvals.approvals);
  const users = useSelector((state) => state.users.users);
  const settings = useSelector((state) => state.settings.company.settings);
  const selectedBoardRedux = useSelector((state) => state.boards.selectedBoard);
  const boards = useSelector((state) => state.boards.boards);
  const latestManualDraftId = useSelector((state) => state.forms.latestManualDraftId);
  const {
    visible: formDrawerVisible = false,
    assignedForm,
    formData,
    cardId: formCardId,
    isEdit: selectedFormIsEdit,
    fileMap,
    ids: preloadedIds,
  } = useSelector((state) => state.boards.formDrawerConfig);
  const {
    visible: formMapDrawerVisible = false,
    selectedCardId: formMapCardId,
  } = useSelector((state) => state.boards.formMapDrawerConfig);

  const [containerWidth, setContainerWidth] = useState(null);
  const [containerHeight, setContainerHeight] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [cardType, setCardType] = useState(null);

  const fullSelectedBoard = useMemo(() => (
    boards[selectedBoardRedux?.id] ?? {}
  ), [selectedBoardRedux, boards]);

  const assignedDraft = useMemo(() => {
    const relevantDraftId = latestManualDraftId ?? assignedForm?.draftId;
    if (!assignedForm) return { draftId: relevantDraftId };
    return {
      ...assignedForm,
      draftId: relevantDraftId,
    };
  }, [assignedForm, latestManualDraftId]);

  const userBoardPermissions = useBoardPermissions({
    users: selectedBoardRedux?.users,
    creatorId: selectedBoard?.creatorId,
  });

  const hideFormDrawer = useCallback(() => dispatch(closeFormDrawer()), [dispatch]);

  const deleteWidget = useCallback((key) => {
    const newCurrentLayout = currentLayout.filter((filteredLayout) => filteredLayout.i !== key);
    setCurrentLayout(newCurrentLayout);
  }, [currentLayout]);

  const getDimensions = useCallback(() => {
    if (containerRef.current) {
      const { offsetWidth, offsetHeight } = containerRef.current;
      setContainerWidth(offsetWidth);
      setContainerHeight(offsetHeight);
    }
  }, []);

  useEffect(() => {
    getDimensions();

    window.addEventListener('resize', getDimensions);

    return () => {
      window.removeEventListener('resize', getDimensions);
    };
  }, [getDimensions]);

  useEffect(() => {
    if (selectedBoard?.id) {
      dispatch(getFormMappings(selectedBoard.id));
    }
  }, [selectedBoard?.id]);

  const rowHeight = (containerHeight - (NUM_COLS * ITEM_MARGIN)) / NUM_COLS;
  return (
    <div style={{ height: '100%', width: '100%' }} ref={containerRef}>
      <GridLayout
        width={containerWidth - rightMargin}
        rowHeight={rowHeight}
        cols={NUM_COLS}
        margin={MARGINS}
        layout={currentLayout}
        isDraggable={isEdit}
        isResizable={isEdit}
        resizeHandles={RESIZE_HANDLES}
        draggableCancel=".dashboard-component-delete-btn"
        onLayoutChange={(newLayout) => {
          // GridLayout doesn't maintain 'settings', so copy them over
          const settingsMap = {};
          currentLayout.forEach(({ i: widgetKey, settings: widgetSettings }) => {
            settingsMap[widgetKey] = widgetSettings;
          });
          const newLayoutWithSettings = newLayout.map((layoutItem) => ({
            ...layoutItem,
            settings: settingsMap[layoutItem.i],
          }));
          setCurrentLayout(newLayoutWithSettings);
        }}
      >
        {
          currentLayout.map((layoutItem) => {
            const [itemType, itemId] = layoutItem.i?.split('-') ?? [];
            switch (itemType) {
              case 'clock':
                return (
                  <div key="clock" className="dashboard-widget-border">
                    <DashboardClock
                      user={ourUsers[0]}
                      settings={settings}
                      phases={phases}
                      projects={projects}
                      unions={unions}
                      onClockIn={onClockIn}
                      onClockOut={onClockOut}
                      onSwitch={onSwitch}
                      onBreakStart={onBreakStart}
                      onBreakEnd={onBreakEnd}
                      onDelete={() => deleteWidget('clock')}
                      isEdit={isEdit}
                    />
                  </div>
                );
              case 'quickStats':
                if (!Permissions.has('DASHBOARD_QUICK_STATS')) return null;
                return (
                  <div key="quickStats" className="dashboard-widget-border">
                    <DashboardQuickStats
                      users={users}
                      onUserClick={onUserClick}
                      approvals={approvals}
                      onDelete={() => deleteWidget('quickStats')}
                      isEdit={isEdit}
                    />
                  </div>
                );
              case 'liveFeed':
                if (!Permissions.has('DASHBOARD_LIVE_FEED')) return null;
                return (
                  <div key="liveFeed" className="dashboard-widget-border">
                    <LiveFeed
                      history={history}
                      onDelete={() => deleteWidget('liveFeed')}
                      isEdit={isEdit}
                    />
                  </div>
                );
              case 'tasks':
                return (
                  <div key="tasks" className="dashboard-widget-border">
                    <DashboardTasksWidget
                      layout={layoutItem}
                      onDelete={() => deleteWidget('tasks')}
                      onEdit={onEditWidget('tasks')}
                      isEdit={isEdit}
                    />
                  </div>
                );
              case 'timeTrackingExports':
              case 'formExports':
              case 'boardExports': {
                const height = (
                  layoutItem.h * containerHeight
                ) / NUM_COLS;
                return (
                  <div key={layoutItem.i} className="dashboard-widget-border">
                    <DashboardCustomExportWidget
                      exportId={itemId}
                      onDelete={() => deleteWidget(layoutItem.i)}
                      isEdit={isEdit}
                      height={height - 200}
                    />
                  </div>
                );
              }
              case 'shortLists':
                return (
                  <div key={layoutItem.i} className="dashboard-widget-border">
                    <DashboardShortListWidget
                      shortListId={itemId}
                      onDelete={() => deleteWidget(layoutItem.i)}
                      isEdit={isEdit}
                      setSelectedBoard={setSelectedBoard}
                      setCardType={setCardType}
                      key={layoutKey}
                    />
                  </div>
                );
              default:
                if (!Permissions.has('MANAGE_ANALYTICS')) return null;
                return (
                  <div key={layoutItem.i} className="dashboard-widget-border">
                    <DashboardAnalyticsWidget
                      analyticsId={layoutItem.i}
                      onDelete={() => deleteWidget(layoutItem.i)}
                      key={`${layoutItem.i}-${layoutKey ?? 'none'}-${selectedDashboardId}`}
                      savedReports={savedReports}
                      analyticsData={analyticsData}
                      type={analyticsType}
                      projectId={projectId}
                      cardId={cardId}
                      isEdit={isEdit}
                    />
                  </div>
                );
            }
          })
        }
      </GridLayout>
      {!cardId && ( // prevent board card drawer from rendering when opening analytics from card
        <>
          <BoardCardDrawer
            boardId={selectedBoard?.id}
            cardTypeId={selectedBoard?.cardTypeId}
            divisions={selectedBoard?.divisions}
            showCardNumber={selectedBoard?.showCardNumber}
            useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
            userBoardPermissions={userBoardPermissions}
          />
          <CustomFieldFormMappingDrawer
            visible={formMapDrawerVisible}
            selectedProfileId={formMapCardId}
            profileTypeId={fullSelectedBoard.cardTypeId}
            divisions={fullSelectedBoard.divisions}
            type="card"
          />
          <FormResponder
            visible={formDrawerVisible}
            onClose={hideFormDrawer}
            assignedForm={assignedDraft}
            preloadedData={formData}
            preloadedIds={preloadedIds}
            isOnEditStep={selectedFormIsEdit}
            cardId={formCardId}
            initialFileMap={fileMap}
          />
        </>
      )}
      {currentLayout.length === 0 && (
        <OnTraccrEmpty
          title="Dashboard"
          description="Edit by clicking the pencil icon in the header and then add widgets to your dashboard to get started."
        />
      )}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
DashboardContainer.propTypes = {
  currentLayout: PropTypes.array.isRequired,
  setCurrentLayout: PropTypes.func.isRequired,
  savedReports: PropTypes.object,
  analyticsData: PropTypes.object,
  selectedDashboardId: PropTypes.string,
  isEdit: PropTypes.bool,
  projectId: PropTypes.string,
  cardId: PropTypes.string,
  analyticsType: PropTypes.string,
  layoutKey: PropTypes.number,
  rightMargin: PropTypes.number,
  ourUsers: PropTypes.array,
  history: PropTypes.object,
  onClockIn: PropTypes.func,
  onClockOut: PropTypes.func,
  onSwitch: PropTypes.func,
  onBreakStart: PropTypes.func,
  onBreakEnd: PropTypes.func,
  onUserClick: PropTypes.func,
  onEditWidget: PropTypes.func,
};

DashboardContainer.defaultProps = {
  savedReports: {},
  analyticsData: {},
  selectedDashboardId: null,
  isEdit: false,
  projectId: undefined,
  cardId: undefined,
  analyticsType: undefined,
  layoutKey: 0,
  rightMargin: 0,
  ourUsers: [],
  history: undefined,
  onClockIn: undefined,
  onClockOut: undefined,
  onSwitch: undefined,
  onBreakStart: undefined,
  onBreakEnd: undefined,
  onUserClick: undefined,
  onEditWidget: undefined,
};
